import React, { ReactNode, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import MissgoAIGuide from "./components/MissgoAIGuide";
import MissgoAIGuideDeep from "./components/MissgoAIGuideDeep";
import MissgoAIChatbot from "./components/MissgoAIChatbot";
import MissgoAILawChatbot from "./components/MissgoAILawChatbot";
import { getUserSubs } from "./utils/useGetSubsState";
import { AuthStatus, useAuthorization } from "./utils/useAuthorization";
import { renderOverlay } from "./utils/authOverlay";
import { usePostFreeTrial } from "./api";
import { useGetTicketCountByType } from "../advertisement/service/ad.api";
import ChatIcon from "./ui/ChatIcon";
import PaperIcon from "./ui/PaperIcon";
import PaperSearchIcon from "./ui/PaperSearchIcon";
import StatuteIcon from "./ui/StatuteIcon";

const MissgoAIDeep = () => {
    const searchParams = useSearchParams();
    const missgoAIType = searchParams[0].get("MissgoAIType") as "guide" | "guide-deep" | "chat" | "chat-statute";

    const { mutate: postFreeTrialMutate } = usePostFreeTrial();
    const userSubs = getUserSubs();
    const { status, AISummaryData, AISummaryDataRefetch, isLogin, chatAvailableCountRefetch } = useAuthorization({
        missgoAIType,
        userSubs,
    });

    const { data: ticketCount } = useGetTicketCountByType(31, missgoAIType === "guide-deep");
    const hasDeepTicket = missgoAIType === "guide-deep" && (ticketCount?.curHaveCnt ?? 0) > 0;

    const isInitialAnimationRef = useRef(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            isInitialAnimationRef.current = false;
            const url = new URL(window.location.href);
            url.searchParams.set("isAnimationEnabled", "false");

            window.history.replaceState(null, "", url.toString());
        }, 350);
        return () => clearTimeout(timer);
    }, []);

    const isAnalysisOrNewItem = status === AuthStatus.ANALYSIS_IN_PROGRESS || status === AuthStatus.NEW_ITEM_ERROR;

    const isUIBlocked = isAnalysisOrNewItem || (!hasDeepTicket && status !== AuthStatus.SUCCESS);
    const isContentBlurred =
        missgoAIType === "guide-deep" ? isAnalysisOrNewItem : status === AuthStatus.NOT_LOGGED_IN || isAnalysisOrNewItem;

    const onPaymentButtonClick = (type?: "upgrade" | "ticket") => {};

    const getHeaderforAI = (): { text: string; color: string; icon: ReactNode } => {
        switch (missgoAIType) {
            case "guide":
                return {
                    text: "AI 경매 길잡이",
                    color: "#3478F6",
                    icon: <PaperIcon color="#FFF" />,
                };
            case "guide-deep":
                return {
                    text: "AI 경매 길잡이 DEEP",
                    color: "#5F0080",
                    icon: <PaperSearchIcon color="#FFF" />,
                };
            case "chat":
                return {
                    text: "AI 챗봇",
                    color: "#F6494B",
                    icon: <ChatIcon color="#FFF" />,
                };
            case "chat-statute":
                return {
                    text: "AI 법령 도우미",
                    color: "#F6A72D",
                    icon: <StatuteIcon color="#FFF" />,
                };
        }
    };

    return (
        <Layout>
            <HeaderBox>
                <Header $backgroundColor={getHeaderforAI().color}>
                    {getHeaderforAI().text}
                    {getHeaderforAI().icon}
                </Header>
            </HeaderBox>

            <ContentWrapper style={{ height: "calc(100vh - 70px)" }}>
                <BlurBox
                    $isBlurred={isContentBlurred}
                    style={{
                        overflowY: missgoAIType === "guide" || missgoAIType === "guide-deep" ? "auto" : "hidden",
                    }}
                >
                    {missgoAIType === "guide" && (
                        <MissgoAIGuide summaryData={AISummaryData?.summary?.data} isSuccess={status === AuthStatus.SUCCESS} />
                    )}

                    {missgoAIType === "chat" && (
                        <div style={{ padding: "0 4px 0 14px" }}>
                            <MissgoAIChatbot onPaymentButtonClick={() => {}} />
                        </div>
                    )}

                    {missgoAIType === "guide-deep" && <MissgoAIGuideDeep />}

                    {missgoAIType === "chat-statute" && (
                        <div style={{ padding: "0 4px 0 14px" }}>
                            <MissgoAILawChatbot onPaymentButtonClick={() => {}} />
                        </div>
                    )}
                </BlurBox>

                {isUIBlocked && (
                    <Overlay>
                        {renderOverlay({
                            status,
                            missgoAIType,
                            onPaymentButtonClick,
                            postFreeTrialMutate,
                        })}
                    </Overlay>
                )}
            </ContentWrapper>
        </Layout>
    );
};

const Layout = styled.div`
    height: 100%;
`;

const ContentWrapper = styled.div`
    flex-grow: 1;
    position: relative;
`;

const BlurBox = styled.div<{ $isBlurred: boolean }>`
    height: 100%;
    filter: blur(${(props) => (props.$isBlurred ? "10px" : "0px")});
`;

const HeaderBox = styled.div`
    width: 100%;
    padding: 0 14px 16px 14px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
`;

const Header = styled.div<{ $backgroundColor: string }>`
    width: 100%;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;

    border-radius: 6px;
    background-color: ${(props) => props.$backgroundColor};

    color: #fff;
    font-size: 14px;
    font-weight: 700;
    line-height: 140%;
`;

const Overlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
`;

export default MissgoAIDeep;
